// ═══════════════════════════════════════════════════════════════════════════
// AUREUS SOCIAL PRO — Sentry Scrubbing RGPD
// Supprime les données belges sensibles AVANT envoi à Sentry.
// Couvre : NISS, IBAN (BE et autres), BCE entreprise, cartes bancaires,
//          mots de passe, tokens, JWT, clés API, emails clients,
//          numéros de téléphone, paths utilisateurs Windows.
//
// ⚠️ Toute donnée scrubbée est remplacée par un placeholder typé.
//    Cette logique tourne CÔTÉ CLIENT ET SERVEUR — pas de regex exotiques.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Patterns belges et internationaux ─────────────────────────────────────
const PATTERNS = [
  // NISS belge : 11 chiffres avec ou sans séparateurs (XX.XX.XX-XXX.XX)
  { re: /\b\d{2}\.\d{2}\.\d{2}[-.\s]?\d{3}\.\d{2}\b/g, replace: '[NISS]' },
  { re: /\b\d{6}[-.\s]?\d{3}[-.\s]?\d{2}\b/g, replace: '[NISS]' },

  // IBAN belge (16 chars) et international (jusqu'à 34)
  { re: /\bBE\d{2}[\s]?\d{4}[\s]?\d{4}[\s]?\d{4}\b/gi, replace: '[IBAN_BE]' },
  { re: /\b[A-Z]{2}\d{2}[\s]?(?:[A-Z0-9][\s]?){10,30}\b/g, replace: '[IBAN]' },

  // BCE (numéro d'entreprise belge) : BE0XXXXXXXXX (anciens) ou BE1XXXXXXXXX (nouveaux)
  { re: /\bBE[\s.]?[01]\d{3}[\s.]?\d{3}[\s.]?\d{3}\b/gi, replace: '[BCE]' },

  // BIC/SWIFT
  { re: /\b[A-Z]{4}BE[A-Z0-9]{2}(?:[A-Z0-9]{3})?\b/g, replace: '[BIC]' },

  // Cartes bancaires (16 chiffres, groupés ou non)
  { re: /\b(?:\d[\s-]?){13,19}\b/g, replace: (m) => /\d{13,19}/.test(m.replace(/[\s-]/g, '')) ? '[CARD]' : m },

  // Téléphones belges : +32, 0032, 04XX XX XX XX
  { re: /(?:^|[^\w+])(\+32|0032|0)[\s.-]?4\d{2}[\s.-]?\d{2}[\s.-]?\d{2}[\s.-]?\d{2}(?!\d)/g, replace: (m) => m.replace(/[\d+]+[\d\s.-]+\d/, '[PHONE]') },

  // JWT (3 segments base64url séparés par des points)
  { re: /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, replace: '[JWT]' },

  // Tokens GitHub / Vercel / Supabase
  { re: /\bgh[pousr]_[A-Za-z0-9]{20,255}\b/g, replace: '[GH_TOKEN]' },
  { re: /\bvcp_[A-Za-z0-9]{20,}\b/g, replace: '[VERCEL_TOKEN]' },
  { re: /\bsbp_[A-Za-z0-9]{20,}\b/g, replace: '[SUPABASE_TOKEN]' },
  { re: /\bsk_(?:live|test)_[A-Za-z0-9]{20,}\b/g, replace: '[STRIPE_KEY]' },
  { re: /\bre_[A-Za-z0-9]{20,}\b/g, replace: '[RESEND_KEY]' },

  // Paths Windows utilisateurs (révèle l'username)
  { re: /[Cc]:\\Users\\[^\\\s"']+/g, replace: 'C:\\Users\\[USER]' },
  { re: /\/(?:Users|home)\/[^\/\s"']+/g, replace: '/home/[USER]' },
];

// Clés sensibles à masquer dans tout objet (sans regarder la valeur)
const SENSITIVE_KEYS = new Set([
  'password', 'pwd', 'pass', 'secret', 'token', 'auth', 'authorization',
  'cookie', 'session', 'api_key', 'apikey', 'private_key', 'privatekey',
  'niss', 'rrn', 'iban', 'bce', 'bic', 'card', 'cvv', 'cvc', 'pin',
  'service_role_key', 'anon_key', 'jwt', 'refresh_token', 'access_token',
]);

/** Scrub une string en remplaçant les patterns sensibles */
export function scrubString(str) {
  if (typeof str !== 'string' || str.length === 0) return str;
  let out = str;
  for (const { re, replace } of PATTERNS) {
    out = out.replace(re, replace);
  }
  return out;
}

/** Scrub récursif d'un objet (Sentry event, extra, contexts, etc.) */
export function scrubObject(obj, depth = 0) {
  if (depth > 8) return '[TOO_DEEP]'; // Garde-fou contre la récursion
  if (obj == null) return obj;
  if (typeof obj === 'string') return scrubString(obj);
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map((v) => scrubObject(v, depth + 1));

  const out = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    // Masquage par clé
    if (SENSITIVE_KEYS.has(lowerKey) || [...SENSITIVE_KEYS].some((k) => lowerKey.includes(k))) {
      out[key] = '[REDACTED]';
      continue;
    }
    out[key] = scrubObject(value, depth + 1);
  }
  return out;
}

/** Hook Sentry beforeSend : scrub les champs critiques d'un event */
export function scrubSentryEvent(event) {
  if (!event) return event;

  // 1. Message principal
  if (event.message) event.message = scrubString(event.message);

  // 2. Exception : values[].value (message), values[].stacktrace.frames[].vars
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubString(ex.value);
      if (ex.stacktrace?.frames) {
        for (const frame of ex.stacktrace.frames) {
          if (frame.vars) frame.vars = scrubObject(frame.vars);
          if (frame.filename) frame.filename = scrubString(frame.filename);
        }
      }
    }
  }

  // 3. Request : URL, query, headers, cookies, data
  if (event.request) {
    if (event.request.url) event.request.url = scrubString(event.request.url);
    if (event.request.query_string) event.request.query_string = scrubString(event.request.query_string);
    if (event.request.headers) event.request.headers = scrubObject(event.request.headers);
    if (event.request.cookies) event.request.cookies = '[REDACTED]';
    if (event.request.data) event.request.data = scrubObject(event.request.data);
  }

  // 4. Breadcrumbs (peuvent contenir des données utilisateur)
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      message: b.message ? scrubString(b.message) : b.message,
      data: b.data ? scrubObject(b.data) : b.data,
    }));
  }

  // 5. Extra et contexts
  if (event.extra) event.extra = scrubObject(event.extra);
  if (event.contexts) event.contexts = scrubObject(event.contexts);

  // 6. User : on garde l'id (interne) mais on scrub email
  if (event.user) {
    if (event.user.email) event.user.email = scrubString(event.user.email);
    if (event.user.ip_address) delete event.user.ip_address;
  }

  return event;
}

/** Hook Sentry beforeBreadcrumb : scrub un breadcrumb */
export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb) return breadcrumb;
  if (breadcrumb.message) breadcrumb.message = scrubString(breadcrumb.message);
  if (breadcrumb.data) breadcrumb.data = scrubObject(breadcrumb.data);
  return breadcrumb;
}
